/**
 * Collapsing duplicate `Centers` documents for display.
 *
 * The Centers collection has grown several documents for the same hospital, so
 * the merkez dropdowns list the same name over and over. The documents cannot
 * be deleted - visits, projects and employees already point at whichever copy
 * happened to be picked at the time - so they are merged for *display only*:
 *
 *   - one option per label, backed by a deterministic canonical document,
 *   - a lookup so an id stored earlier still resolves to the option shown,
 *   - the full id list per option, so a selection can be matched or cleared
 *     whichever copy it was originally saved against.
 *
 * Nothing here writes to Firestore. Existing references are left exactly as
 * they are; only newly picked values use the canonical id.
 */

/** Turkish-aware key for comparing labels, ignoring case and stray spacing. */
const normalizeLabel = (value) =>
  (typeof value === 'string' ? value : '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('tr-TR');

/**
 * Group centers by the label the user actually sees, so two centers are only
 * ever merged when they are indistinguishable on screen.
 *
 * @param {object[]} centers   center documents, each with an `id`
 * @param {function} getLabel  builds the display label for a center
 * @returns {{options: object[], canonicalIdById: Map, idsByCanonicalId: Map}}
 *          `options` carries a `duplicateIds` array of every id it stands for.
 */
export const dedupeCenters = (centers, getLabel) => {
  const groups = new Map();

  (centers || []).forEach((center) => {
    if (!center?.id) return;
    const key = normalizeLabel(getLabel(center));
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(center);
  });

  const options = [];
  const canonicalIdById = new Map();
  const idsByCanonicalId = new Map();

  groups.forEach((members) => {
    // Sorting by document id keeps the canonical copy identical for every user
    // and stable between sessions.
    const sorted = [...members].sort((a, b) => String(a.id).localeCompare(String(b.id)));
    const canonical = sorted[0];
    const ids = sorted.map((member) => member.id);

    options.push({ ...canonical, duplicateIds: ids });
    idsByCanonicalId.set(canonical.id, ids);
    ids.forEach((id) => canonicalIdById.set(id, canonical.id));
  });

  return { options, canonicalIdById, idsByCanonicalId };
};

/** Resolve any center id to the id of the option that represents it. */
export const resolveCanonicalCenterId = (canonicalIdById, centerId) =>
  centerId ? canonicalIdById.get(centerId) || centerId : centerId;

/** `true` when any id this option stands for appears in `centerIds`. */
export const optionMatchesAnyCenterId = (option, centerIds) => {
  if (!centerIds || centerIds.length === 0) return false;
  const ids = option?.duplicateIds || [option?.id];
  return ids.some((id) => centerIds.includes(id));
};
